import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, ArrowRight, Zap, Bot, BarChart3, Sparkles } from "lucide-react";
import { getSeoPage, seoPages, SITE } from "../data/seoPages";

const STATS = [
  { value: "1000+", label: "Brands Onboarded" },
  { value: "98%", label: "First-Time Approval Rate" },
  { value: "14 Days", label: "Standard Launch Time" },
  { value: "70+", label: "Marketplaces Supported" },
  { value: "90 Sec", label: "AI Listing Generation" },
  { value: "1000+", label: "Catalogs Managed" },
];

const COMPARISON = [
  ["Time per SKU", "30–45 minutes", "~90 seconds"],
  ["QC rejection risk", "High (manual errors)", "Minimal (template-validated)"],
  ["SEO keyword coverage", "Inconsistent", "Systematic, every listing"],
  ["Bulk catalogs (500 SKUs)", "3–4 weeks", "Within a day"],
  ["Marketplace template compliance", "Manual lookup", "Built-in for 70+ platforms"],
  ["Cost per listing", "₹50–150 labor + rework", "Fraction, zero rework"],
];

const HOW_STEPS = [
  { t: "Upload Product Data", d: "Add product images and basic details — brand, MRP, sizes." },
  { t: "AI Generates the Listing", d: "Computer vision extracts attributes; AI writes SEO-optimized, marketplace-compliant content." },
  { t: "Automatic Quality Check", d: "Every mandatory attribute and guideline is validated against the target marketplace's rules." },
  { t: "Go Live", d: "Download the upload-ready catalog file or let BrandBorn's team take it live for you." },
];

export default function SeoSoftwarePage() {
  const { slug } = useParams();
  const page = getSeoPage(slug);
  if (!page) return <Navigate to="/software" replace />;

  const url = `${SITE}/software/${page.slug}`;
  const related = page.rel.map(getSeoPage).filter(Boolean);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE },
          { "@type": "ListItem", position: 2, name: "Software", item: `${SITE}/software` },
          { "@type": "ListItem", position: 3, name: page.kw, item: url },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: page.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      page.type === "software"
        ? {
            "@type": "SoftwareApplication",
            name: `EcomCatalog.ai — ${page.kw}`,
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            description: page.summary,
            url: "https://ecomcatalog.ai",
            offers: { "@type": "Offer", price: "0", priceCurrency: "INR", description: "Free demo available" },
            creator: { "@type": "Organization", name: "BrandBorn", url: SITE, telephone: "+91-9464645491" },
            aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", ratingCount: "1000", bestRating: "5" },
          }
        : {
            "@type": "Service",
            name: page.kw,
            serviceType: page.kw,
            description: page.summary,
            areaServed: { "@type": "Country", name: "India" },
            provider: { "@type": "Organization", name: "BrandBorn", url: SITE, telephone: "+91-9464645491" },
          },
      {
        "@type": "HowTo",
        name: `How ${page.kw} works with BrandBorn`,
        step: HOW_STEPS.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.t, text: s.d })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="seo-software-page">
      <Helmet>
        <title>{page.title}</title>
        <meta name="description" content={page.desc} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.desc} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={page.title} />
        <meta name="twitter:description" content={page.desc} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="px-4 py-14 md:py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <nav aria-label="Breadcrumb" className="text-xs text-gray-400 mb-4 font-semibold">
            <Link to="/" className="hover:text-red-500">Home</Link> / <Link to="/software" className="hover:text-red-500">Software</Link> / <span className="text-gray-600">{page.kw}</span>
          </nav>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 text-blue-700 rounded-full text-xs font-bold mb-5">
            <Bot size={14} /> {page.cluster.toUpperCase()} • BY BRANDBORN & ECOMCATALOG.AI
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4 leading-tight">{page.h1}</h1>
          <p className="text-[11px] text-gray-400 font-bold mb-3" data-testid="seo-last-updated">Last updated: June 2026 • Software docs v2</p>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">{page.sub}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://ecomcatalog.ai" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-blue-600 text-white rounded-2xl font-black text-sm uppercase tracking-wider hover:bg-blue-700 hover:-translate-y-0.5 transition-all shadow-lg" data-testid="seo-page-software-cta">
              Try EcomCatalog.ai Free
            </a>
            <Link to="/#contact" className="px-8 py-4 bg-red-600 text-white rounded-2xl font-black text-sm uppercase tracking-wider hover:bg-red-700 hover:-translate-y-0.5 transition-all shadow-lg" data-testid="seo-page-contact-cta">
              Talk to an Expert
            </Link>
          </div>
        </div>
      </section>

      {/* AI-Readable Summary */}
      <section className="px-4 py-10">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-950 text-white rounded-3xl p-8 border border-gray-800" data-testid="ai-summary-block">
            <div className="flex items-center gap-2 text-xs font-bold text-blue-400 uppercase tracking-widest mb-3"><Sparkles size={14} /> Quick Answer</div>
            <p className="text-gray-200 leading-relaxed">{page.summary}</p>
          </div>
        </div>
      </section>

      {/* Definition */}
      <section className="px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-4">What is {page.def.term}?</h2>
          <p className="text-gray-600 leading-relaxed text-lg">{page.def.text}</p>
        </div>
      </section>

      {/* Stats */}
      <section className="px-4 py-10 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-black text-gray-900 text-center mb-8">BrandBorn + EcomCatalog.ai by the Numbers</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {STATS.map((s, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 text-center border border-gray-100 shadow-sm">
                <div className="text-2xl font-black text-red-600">{s.value}</div>
                <div className="text-xs text-gray-500 font-semibold mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-8">How {page.kw} Works</h2>
          <div className="space-y-5">
            {HOW_STEPS.map((s, i) => (
              <div key={i} className="flex gap-4 items-start">
                <div className="w-9 h-9 rounded-full bg-red-600 text-white font-black flex items-center justify-center flex-shrink-0">{i + 1}</div>
                <div>
                  <h3 className="font-black text-gray-900">{s.t}</h3>
                  <p className="text-gray-600 text-sm mt-1">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="px-4 py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-6">Manual Listing vs AI Listing</h2>
          <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-900 text-white text-left">
                  <th className="px-5 py-3 font-black">Factor</th>
                  <th className="px-5 py-3 font-black">Manual Process</th>
                  <th className="px-5 py-3 font-black text-blue-300">With AI ({page.kw})</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row, i) => (
                  <tr key={i} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                    <td className="px-5 py-3 font-bold text-gray-800">{row[0]}</td>
                    <td className="px-5 py-3 text-gray-500">{row[1]}</td>
                    <td className="px-5 py-3 font-semibold text-blue-700">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Entity block */}
      <section className="px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-black text-gray-900 mb-4">About BrandBorn & EcomCatalog.ai</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            <strong className="text-gray-900">BrandBorn</strong> is India's #1 brand onboarding agency and an{" "}
            <strong className="text-gray-900">Official OMS Partner of Myntra</strong>, with 1000+ brands launched across 70+ marketplaces at a 98% first-time approval rate.
            BrandBorn built <a href="https://ecomcatalog.ai" target="_blank" rel="noopener noreferrer" className="font-bold text-blue-600 hover:underline">EcomCatalog.ai</a> —{" "}
            <strong className="text-gray-900">India's first AI auto listings and data analysis software</strong> — and has been featured in{" "}
            <strong className="text-gray-900">The Times of India</strong> and <strong className="text-gray-900">The Economic Times</strong>.
          </p>
          <div className="flex flex-wrap gap-2">
            {["Marketplace Onboarding", "AI Listing", "Catalog Management", "Data Analytics", "Marketplace Automation", "Quick Commerce"].map((e, i) => (
              <span key={i} className="px-3 py-1.5 bg-slate-100 text-gray-700 rounded-full text-xs font-bold border border-slate-200">{e}</span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="px-4 py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-8">Frequently Asked Questions — {page.kw}</h2>
          <div className="space-y-4">
            {page.faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-testid={`seo-faq-${i}`}>
                <h3 className="font-black text-gray-900 mb-2 flex items-start gap-2"><CheckCircle size={18} className="text-green-500 mt-0.5 flex-shrink-0" />{f.q}</h3>
                <p className="text-gray-600 text-sm leading-relaxed pl-6">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related / Internal linking */}
      <section className="px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl font-black text-gray-900 mb-5">Related Software & Services</h2>
          <div className="grid sm:grid-cols-2 gap-3 mb-8">
            {related.map((r) => (
              <Link key={r.slug} to={`/software/${r.slug}`} className="flex items-center justify-between gap-2 p-4 bg-slate-50 hover:bg-red-50 border border-gray-100 hover:border-red-200 rounded-xl font-bold text-sm text-gray-800 transition-colors" data-testid={`related-link-${r.slug}`}>
                {r.kw} <ArrowRight size={16} className="text-red-500 flex-shrink-0" />
              </Link>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 text-sm font-bold">
            <Link to="/software" className="text-blue-600 hover:underline">All Software →</Link>
            <Link to="/knowledge" className="text-blue-600 hover:underline">Knowledge Base →</Link>
            <Link to="/services" className="text-blue-600 hover:underline">Onboarding Services →</Link>
            <Link to="/blog" className="text-blue-600 hover:underline">Blog →</Link>
            <Link to="/growth-story" className="text-blue-600 hover:underline">Client Results →</Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-14 bg-gradient-to-r from-red-600 to-orange-500">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-black text-white mb-4">Ready to Automate with {page.kw}?</h2>
          <p className="text-white/90 mb-6">Join 1000+ brands using BrandBorn's AI to launch and grow on Indian marketplaces.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://ecomcatalog.ai" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-white text-red-600 rounded-2xl font-black text-sm uppercase tracking-wider hover:-translate-y-0.5 transition-transform shadow-xl">Start Free with AI</a>
            <a href="tel:+919464645491" className="px-8 py-4 bg-black text-white rounded-2xl font-black text-sm uppercase tracking-wider hover:-translate-y-0.5 transition-transform shadow-xl">Call +91-9464645491</a>
          </div>
        </div>
      </section>
    </div>
  );
}
